import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import Disclosure from "@/components/Disclosure";
import Rubric from "@/components/Rubric";
import VerdictBlock from "@/components/VerdictBlock";
import AceSchema from "@/components/AceSchema";
import { SITE_URL } from "@/lib/site";
import { itemListSchema, articleSchema } from "@/components/schema";
import { SPAIN_CITY_GROUPS } from "@/data/clinics-spain";

const PATH = "/best-smile-makeover-clinics-in-spain";
const TITLE =
  "Smile-makeover clinics in Spain — a national editorial guide";
const DESCRIPTION =
  "A national editorial guide to choosing a smile-makeover clinic in Spain. Clinics grouped by city — Madrid, Barcelona, Costa del Sol, Valencia, Seville — and evaluated on the same rubric.";
const PUBLISHED = "2026-04-18";
const MODIFIED = "2026-04-18";

export default function BestSmileMakeoverClinicsInSpain() {
  // Flatten every clinic across every city into a single ItemList, so
  // the schema lines up with the rendered hub.
  const flatClinics = SPAIN_CITY_GROUPS.flatMap((g) =>
    g.clinics.map((c) => ({
      name: c.name,
      url: `${SITE_URL}${PATH}#${c.slug}`,
      description: c.profile,
    })),
  );
  const itemList = itemListSchema(flatClinics, PATH);

  const article = articleSchema({
    title: TITLE,
    description: DESCRIPTION,
    path: PATH,
    datePublished: PUBLISHED,
    dateModified: MODIFIED,
  });

  return (
    <>
      <SEO title={TITLE} description={DESCRIPTION} path={PATH} type="article" />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(article)}</script>
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
      </Helmet>

      <AceSchema />

      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Spain", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="National guide"
        title="Smile-makeover clinics in Spain — a national editorial guide"
        standfirst="A country-level orientation for readers choosing a smile-makeover clinic in Spain. Clinics grouped by city, each profiled on the same rubric, with links down to the per-city satellite evaluations for deeper regional coverage."
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
        readMinutes={20}
      />

      <div className="container-prose prose-editorial mt-10">
        <Disclosure />

        <VerdictBlock />

        <h2>What this page is</h2>
        <p>
          This is a top-of-funnel national orientation, not a
          replacement for the city-level satellite pieces below. A
          reader who already knows they want to fly into Málaga and
          treat locally on the Costa del Sol should go directly to our{" "}
          <Link to="/best-veneer-clinics-in-marbella">
            Marbella evaluation
          </Link>
          . A reader whose case is in Madrid should read the{" "}
          <Link to="/best-veneer-clinics-in-madrid">Madrid evaluation</Link>
          . This page exists for the reader earlier in the process —
          the one still weighing which Spanish city to go to, and why.
        </p>
        <p>
          The goal here is not to rank clinics or cities. It is to
          teach the reader how to think about the market — where the
          genuine strengths of each region lie, what the rubric
          surfaces in each, and how to narrow a long list to a
          shortlist they can pursue in person.
        </p>

        <h2>How to think about choosing a smile-makeover clinic in Spain</h2>
        <p>
          Spain does not have one cosmetic-dentistry market. It has
          five or six distinct regional markets, each with its own
          structural tendencies. A veneer case in Barcelona is
          shopped in a different way than a veneer case in Marbella,
          even when the clinical work itself is closely comparable.
          Understanding the regional shape of each market is the first
          real decision a reader makes — before any specific clinic
          enters the shortlist.
        </p>
        <p>
          Three regional patterns are worth knowing.
        </p>
        <p>
          <strong>The capital markets (Madrid and Barcelona)</strong>
          {" "}tend to contain both long-tenured generational family
          practices and newer studio-scale aesthetic-dentistry
          operations. The range within either city is wider than a
          reader might expect; two clinics on the same short list can
          represent quite different models. Multi-specialty scale,
          surgeon-educator credentials, and specific digital-workflow
          claims are more common in the capitals than elsewhere.
        </p>
        <p>
          <strong>The Mediterranean coast (Costa del Sol, parts of
          Valencia)</strong>{" "}supports clinics that have invested in
          cross-border patient flow — multilingual intake, structured
          pre-consult, post-treatment remote follow-up. The clinics
          here are operationally set up for international patients in
          a way that is not universally true of the capital markets.
          Málaga airport in particular has produced a cluster of
          clinics that treat the two-visit, cross-border veneer case
          as a standard product rather than an exception.
        </p>
        <p>
          <strong>The secondary markets (Seville, parts of
          Valencia)</strong>{" "}contain a mix of multi-generational
          Andalusian and Mediterranean family practices, some with
          genuinely serious clinical work that is less visible on
          international aggregator sites. The named-practitioner
          model — a clinic built around one clinician's academic and
          professional profile — is more common in these markets than
          in the larger cities.
        </p>

        <h2>The rubric</h2>
        <p>
          The full rubric lives on our{" "}
          <Link to="/methodology">methodology page</Link>. It applies
          the same twelve dimensions to every clinic on this site, in
          the same order, whether the clinic is in Marbella, Madrid,
          Barcelona, Valencia or Seville. Summarised:
        </p>
        <Rubric variant="summary" />
        <p className="mt-6">
          At a national level, the dimensions that most consistently
          differentiate clinics across Spain are lab model and
          ceramist proximity (who actually handles the porcelain, and
          where), published preparation philosophy (what the clinic
          believes about tooth reduction, written down and public),
          and the authorial structure of the clinic (does a named
          clinician carry accountability for the case, or does the
          case sit inside a larger multi-disciplinary operation).
        </p>

        <h2>Clinics by city</h2>
        <p>
          The groupings below are orientation, not exhaustive
          evaluation. Each clinic is profiled briefly and linked to
          its own website; readers who want the full rubric applied
          per clinic should follow the link to that city's satellite
          evaluation. Order within each city group does not imply
          ranking.
        </p>

        {SPAIN_CITY_GROUPS.map((group) => (
          <section key={group.city} className="border-t border-rule pt-10">
            <div className="eyebrow mb-2">{group.regionLabel}</div>
            <h3 className="font-serif text-h2 text-ink">
              {group.city}
            </h3>
            <p className="mt-3 max-w-2xl text-ink/80">{group.standfirst}</p>

            {group.satellitePath ? (
              <p className="mt-4">
                <Link
                  to={group.satellitePath}
                  className="font-sans text-small"
                >
                  Read the {group.city} evaluation in full &rarr;
                </Link>
              </p>
            ) : null}

            <ol className="mt-8 grid gap-8 md:grid-cols-1">
              {group.clinics.map((clinic) => (
                <li
                  key={`${group.city}-${clinic.slug}`}
                  id={clinic.slug}
                  className="border-t border-rule pt-6"
                >
                  <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between">
                    <h4 className="font-serif text-[1.4rem] leading-tight text-ink">
                      {clinic.name}
                    </h4>
                    <a
                      href={clinic.website}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="font-sans text-small"
                    >
                      Visit website &rarr;
                    </a>
                  </div>
                  <p className="mt-3 text-ink">{clinic.profile}</p>
                  <p className="mt-3 text-small text-ink/80">
                    <span className="eyebrow mr-2">Strongest on</span>
                    {clinic.strongestOn}
                  </p>
                </li>
              ))}
            </ol>
          </section>
        ))}

        <h2 className="mt-16">How to move from this page to a shortlist</h2>
        <p>
          A reader using this page well will do three things in order.
          First, pick the region — not the clinic. The trip, the
          logistics, the language of intake, and the post-treatment
          follow-up are all region-shaped decisions before they are
          clinic-shaped decisions. Second, open the per-city
          evaluation for that region and read the full profile for
          every clinic covered. Each city satellite applies the rubric
          in depth; this hub deliberately does not. Third, only then
          compare two or three clinics directly, using the dimensions
          the rubric makes visible.
        </p>
        <p>
          A shortlist built this way is meaningfully different from a
          shortlist built on Instagram grids or aggregator-site
          rankings. It is slower. It is also considerably more
          defensible when the reader sits in the first consultation
          and has to decide whether the clinician in front of them
          matches the published positioning.
        </p>

        <h2>Related reading</h2>
        <p>
          The decision framework that produced the rubric is on{" "}
          <Link to="/how-to-choose-a-smile-makeover-clinic-in-spain">
            how to choose a smile-makeover clinic in Spain
          </Link>
          . The vocabulary used throughout the per-city evaluations is
          developed in{" "}
          <Link to="/natural-vs-overdone-veneers">
            natural versus overdone veneers
          </Link>
          {" "}and{" "}
          <Link to="/minimal-prep-vs-overprepped-veneers">
            minimal-prep versus over-prepped veneers
          </Link>
          . The per-city satellite evaluations are:
        </p>
        <ul>
          <li>
            <Link to="/best-veneer-clinics-in-madrid">Madrid</Link>
          </li>
          <li>
            <Link to="/best-veneer-clinics-in-barcelona">Barcelona</Link>
          </li>
          <li>
            <Link to="/best-veneer-clinics-in-marbella">
              Marbella / Costa del Sol
            </Link>
          </li>
          <li>
            <Link to="/best-veneer-clinics-in-valencia">Valencia</Link>
          </li>
          <li>
            <Link to="/best-veneer-clinics-in-seville">Seville</Link>
          </li>
        </ul>

        <hr className="rule" />

        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>
              2026-04-18 — First publication. Five city groupings.
              Clinic entries verified against the named clinic's own
              website at the time of writing; entries flagged{" "}
              <code>verified: false</code> in the source data await
              editor confirmation of current operational status.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}
